/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useOrganization } from '@/hooks/useOrganization';
import { supabase } from '@/lib/supabase';

const TIMEZONES = [
  { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'UTC', label: 'UTC' },
];

const CURRENCIES = [
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
];

// Use relative path to go through Vite proxy
const API_URL: string = import.meta.env.VITE_API_URL ?? '/api';

export function OnboardingPage() {
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [timezone, setTimezone] = useState('America/Toronto');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshOrganizations } = useOrganization();

  const createOrganization = useCallback(async (data: { name: string; currency: string; timezone: string }): Promise<{ id: number; uuid: string; name: string }> => {
    console.log('[OnboardingPage] createOrganization called with:', data);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('[OnboardingPage] No session found');
      throw new Error('Not authenticated');
    }

    const payload = {
      name: data.name,
      slug: data.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      settings: JSON.stringify({ currency: data.currency, timezone: data.timezone }),
    };
    console.log('[OnboardingPage] Sending payload:', payload);

    const response = await fetch(`${API_URL}/organizations/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    console.log('[OnboardingPage] Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[OnboardingPage] Error response:', errorText);
      throw new Error(`Failed to create organization: ${response.status} ${errorText}`);
    }

    interface OrganizationResponse {
      id: number;
      uuid: string;
      name: string;
    }
    const result = (await response.json()) as OrganizationResponse;
    console.log('[OnboardingPage] Organization created:', result);
    return result;
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    console.log('[OnboardingPage] Submit button clicked, name:', name);

    try {
      await createOrganization({ name, currency, timezone });
      console.log('[OnboardingPage] Organization created, refreshing organizations...');
      
      // Refresh organizations so OnboardingCheck sees the new organization
      await refreshOrganizations();
      
      console.log('[OnboardingPage] Organizations refreshed, navigating to /app');
      // Use replace to prevent going back to onboarding
      navigate('/app', { replace: true });
    } catch (err) {
      console.error('[OnboardingPage] Submit error:', err);
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  }, [createOrganization, name, currency, timezone, refreshOrganizations, navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground">
            Welcome to <span className="text-primary">BeanGo</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            Let's set up your first organization
          </p>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                Organization Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="My Company"
              />
              <p className="mt-1 text-sm text-muted-foreground">
                This will appear on your invoices and reports.
              </p>
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-foreground mb-1">
                Default Currency
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-foreground mb-1">
                Timezone
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={loading || !name}
              className="w-full bg-primary text-primary-foreground py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          You can change these settings later in your organization settings.
        </p>
      </div>
    </div>
  );
}